import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth';
import { User } from '../types/user';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  refreshUser: () => void;
  handleLoginSuccess: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = () => {
    const currentUser = authService.getCurrentUser();
    const isAuth = authService.isAuthenticated();
    const hasToken = !!localStorage.getItem('access_token');
    
    console.log('Refreshing user state:', { 
      currentUser: currentUser?.name || 'none', 
      userRole: currentUser?.role || 'none',
      isAuth, 
      hasToken,
      currentUserInState: user?.name || 'none'
    });
    
    if (currentUser && isAuth && hasToken) {
      console.log('Setting user in context:', currentUser);
      setUser(currentUser);
    } else {
      console.log('Clearing user from context - missing:', {
        missingUser: !currentUser,
        missingAuth: !isAuth, 
        missingToken: !hasToken,
        currentPath: window.location.pathname
      });
      
      // Only clear if we're definitely not authenticated AND not in the middle of login
      const isLoginFlow = window.location.pathname.includes('login') || window.location.pathname.includes('dashboard');
      
      if (!hasToken && !isLoginFlow) {
        console.log('AuthContext: Clearing user from context');
        setUser(null);
      } else if (!hasToken && isLoginFlow) {
        console.log('AuthContext: Not clearing user during login flow');
      }
    }
  };

  useEffect(() => {
    console.log('AuthContext: useEffect running - checking auth state on mount');
    // Check if user is logged in on mount
    const initAuth = () => {
      refreshUser();
      // Small delay to ensure state updates have propagated
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    };
    
    initAuth();

    // Listen for storage changes (for logout in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      console.log('AuthContext: Storage change detected:', e.key);
      if (e.key === 'access_token' || e.key === 'user') {
        console.log('AuthContext: Calling refreshUser due to storage change');
        refreshUser();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Even if logout fails, clear local state
      console.error('Logout failed:', error);
    } finally {
      // Always clear user state and redirect
      setUser(null);
      // Use window.location for reliable redirect with basename awareness
      const basename = window.location.pathname.startsWith('/rayyi7-dev') ? '/rayyi7-dev' : 
                       window.location.pathname.startsWith('/dev') ? '/dev' : '';
      window.location.href = basename + '/login';
    }
  };

  const handleLoginSuccess = (userData: User) => {
    console.log('AuthContext: Login success - setting user in context:', userData.name, userData.role);
    setUser(userData);
    
    // Verify localStorage has the data IMMEDIATELY
    const hasToken = !!localStorage.getItem('access_token');
    const hasUser = !!localStorage.getItem('user');
    console.log('AuthContext: Post-login storage check:', { hasToken, hasUser });
    
    if (!hasToken || !hasUser) {
      console.error('AuthContext: CRITICAL - Auth data missing from localStorage immediately after login!');
      console.log('AuthContext: Available storage keys:', Object.keys(localStorage));
    }
    
    // Don't call refreshUser() here as it might clear the context if storage is empty
    // The user is already set in the context above
    console.log('AuthContext: Skipping delayed refreshUser to prevent clearing context');
  };

  const value = {
    user,
    isAuthenticated: !!user && authService.isAuthenticated(),
    isLoading,
    logout,
    refreshUser,
    handleLoginSuccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};